// Copyright (c) 2015-present Mattermost, Inc. All Rights Reserved.
// See LICENSE.txt for license information.

import React, {type ComponentType, forwardRef, memo} from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import {
    FixedSizeList,
    type ListChildComponentProps,
    type ListItemKeySelector,
    type ListOnScrollProps,
} from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

import throttle from 'lodash/throttle';

import {useEventCallback} from 'usehooks-ts';

import noop from '@tinkoff/utils/function/noop';

import {type CustomEmoji, type EmojiCategory} from 'mattermost-redux/types/emojis';
import {type ServerError} from 'mattermost-redux/types/errors';

import {type CategoryOrEmojiRow} from 'components/emoji_picker/types';
import {
    CUSTOM_EMOJIS_PER_PAGE,
    EMOJI_CONTAINER_HEIGHT,
    EMOJI_ROWS_OVERSCAN_COUNT,
    EMOJI_SCROLL_THROTTLE_DELAY,
    ITEM_HEIGHT,
} from 'components/emoji_picker/constants';
import {isCategoryHeaderRow} from 'components/emoji_picker/utils';

interface Props {
    categoryOrEmojisRows: CategoryOrEmojiRow[];
    isFiltering: boolean;
    activeCategory: EmojiCategory;
    customEmojisEnabled: boolean;
    customEmojiPage: number;
    setActiveCategory: (category: EmojiCategory) => void;
    incrementEmojiPickerPage: () => void;
    getCustomEmojis: (page?: number, perPage?: number, sort?: string, loadUsers?: boolean) => Promise<{
        data: CustomEmoji[];
        error: ServerError;
    }>;
    EmojiRow: ComponentType<ListChildComponentProps<CategoryOrEmojiRow[]>>;
}

const EmojiPickerCurrentResults = forwardRef<InfiniteLoader, Props>(({
    categoryOrEmojisRows,
    EmojiRow,
    isFiltering,
    activeCategory,
    customEmojisEnabled,
    customEmojiPage,
    setActiveCategory,
    getCustomEmojis,
    incrementEmojiPickerPage,
}: Props, ref) => {
    // Function to create unique key for each row
    const getItemKey = (index: Parameters<ListItemKeySelector>[0], rowsData: Parameters<ListItemKeySelector<CategoryOrEmojiRow[]>>[1]) => {
        const data = rowsData[index];

        if (isCategoryHeaderRow(data)) {
            const categoryRow = data.items[0];
            return `${categoryRow.categoryIndex}-${categoryRow.categoryName}`;
        }

        const emojisRow = data.items;
        const emojiNamesArray = emojisRow.map((emoji) => `${emoji.categoryIndex}-${emoji.emojiId}`);
        return emojiNamesArray.join('--');
    };

    const handleScroll = (scrollOffset: ListOnScrollProps['scrollOffset'], activeCategory: EmojiCategory, isFiltering: boolean, categoryOrEmojisRows: CategoryOrEmojiRow[]) => {
        if (isFiltering) {
            return;
        }

        const approxRowsFromTop = Math.ceil(scrollOffset / ITEM_HEIGHT);
        const closestCategory = categoryOrEmojisRows?.[approxRowsFromTop]?.items[0]?.categoryName;

        if (closestCategory === activeCategory || !closestCategory) {
            return;
        }

        setActiveCategory(closestCategory);
    };

    const throttledScroll = useEventCallback(throttle(
        ({scrollOffset}: ListOnScrollProps) => {
            handleScroll(scrollOffset, activeCategory, isFiltering, categoryOrEmojisRows);
        },
        EMOJI_SCROLL_THROTTLE_DELAY,
        {leading: false, trailing: true},
    ));

    const handleIsItemLoaded = (index: number): boolean => {
        return index < categoryOrEmojisRows.length;
    };

    const handleLoadMoreItems = async () => {
        if (customEmojisEnabled === false) {
            return;
        }

        const {data} = await getCustomEmojis(customEmojiPage, CUSTOM_EMOJIS_PER_PAGE);

        // If data came back empty, or data is less than the perPage, then we know there are no more pages
        if (!data || data.length < CUSTOM_EMOJIS_PER_PAGE) {
            return;
        }

        incrementEmojiPickerPage();
    };

    return (
        <div
            className='emoji-picker__items'
            style={{height: EMOJI_CONTAINER_HEIGHT}}
        >
            <div className='emoji-picker__container'>
                <AutoSizer>
                    {({height, width}) => (
                        <InfiniteLoader
                            ref={ref}
                            itemCount={categoryOrEmojisRows.length + 1} // +1 for the loading row
                            isItemLoaded={handleIsItemLoaded}
                            loadMoreItems={isFiltering ? noop : handleLoadMoreItems}
                        >
                            {({onItemsRendered, ref}) => (
                                <FixedSizeList
                                    ref={ref}
                                    onItemsRendered={onItemsRendered}
                                    height={height}
                                    width={width}
                                    layout='vertical'
                                    overscanCount={EMOJI_ROWS_OVERSCAN_COUNT}
                                    itemCount={categoryOrEmojisRows.length}
                                    itemData={categoryOrEmojisRows}
                                    itemKey={getItemKey}
                                    itemSize={ITEM_HEIGHT}
                                    onScroll={throttledScroll}
                                >
                                    {EmojiRow}
                                </FixedSizeList>
                            )}
                        </InfiniteLoader>
                    )}
                </AutoSizer>
            </div>
        </div>
    );
});

EmojiPickerCurrentResults.displayName = 'EmojiPickerCurrentResults';

export default memo(EmojiPickerCurrentResults);
